import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const inputStyle = {
  width: '100%', padding: '0.6rem 0.8rem', borderRadius: '8px',
  border: '1px solid var(--border, #2a2d3a)',
  background: 'var(--bg-elevated, #252836)',
  color: 'var(--text-primary, #e2e8f0)', fontSize: '0.9rem',
  outline: 'none', boxSizing: 'border-box',
};

export default function Login() {
  const { login, verify2fa, sendSms2fa, verifySms2fa } = useAuth();

  // Étape 1 : identifiants
  const [loginVal, setLoginVal] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Étape 2 : 2FA
  const [step, setStep]           = useState('credentials'); // 'credentials' | 'totp' | 'sms'
  const [tempToken, setTempToken] = useState('');
  const [hasPhone, setHasPhone]   = useState(false);
  const [otpCode, setOtpCode]     = useState('');
  const [smsSent, setSmsSent]     = useState(false);

  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmitCredentials(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(loginVal, password);
      if (result?.requires_2fa) {
        setTempToken(result.temp_token);
        setHasPhone(result.has_phone);
        setStep('totp');
      }
      // Si pas de 2FA → AuthContext a déjà connecté l'utilisateur
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyTotp(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2fa(tempToken, otpCode);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSendSms() {
    setError('');
    setLoading(true);
    try {
      await sendSms2fa(tempToken);
      setSmsSent(true);
      setStep('sms');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifySms(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifySms2fa(tempToken, otpCode);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const cardStyle = {
    background: 'var(--bg-card, #1a1d27)', border: '1px solid var(--border, #2a2d3a)',
    borderRadius: '12px', padding: '2.5rem', width: '100%', maxWidth: '380px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  };

  const logo = (
    <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
      <img src="/logo.png" alt="CEJ Concept"
        style={{ width: 200, height: 'auto', objectFit: 'contain', borderRadius: '10px', marginBottom: '0.75rem' }} />
      <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary, #64748b)', marginTop: '0.25rem' }}>
        Gestion Formation
      </p>
    </div>
  );

  const errorBox = error && (
    <div style={{
      background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: '8px', padding: '0.6rem 0.8rem', marginBottom: '1rem',
      color: '#f87171', fontSize: '0.85rem',
    }}>
      {error}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base, #0f1117)' }}>

      {/* ── Étape 1 : identifiants ── */}
      {step === 'credentials' && (
        <div style={cardStyle}>
          {logo}
          <form onSubmit={handleSubmitCredentials}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary, #94a3b8)', marginBottom: '0.4rem' }}>
                Login
              </label>
              <input type="text" value={loginVal} onChange={e => setLoginVal(e.target.value)} required autoFocus style={inputStyle} />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary, #94a3b8)', marginBottom: '0.4rem' }}>
                Mot de passe
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  required style={{ ...inputStyle, padding: '0.6rem 2.4rem 0.6rem 0.8rem' }}
                />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#ffffff', display: 'flex', alignItems: 'center' }} title={showPassword ? 'Masquer' : 'Afficher'}>
                  {showPassword
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>
            {errorBox}
            <button type="submit" disabled={loading} style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', background: 'var(--accent-blue, #3b82f6)', color: '#fff', border: 'none', fontWeight: 600, fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
      )}

      {/* ── Étape 2 : code TOTP (Microsoft Authenticator) ── */}
      {step === 'totp' && (
        <div style={cardStyle}>
          {logo}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔐</div>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.4rem' }}>Double authentification</h2>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0 }}>
              Entrez le code affiché dans<br /><strong>Microsoft Authenticator</strong>
            </p>
          </div>
          <form onSubmit={handleVerifyTotp}>
            <input
              type="text" inputMode="numeric" pattern="[0-9 ]*" autoFocus
              value={otpCode} onChange={e => setOtpCode(e.target.value)}
              placeholder="000 000" maxLength={7}
              style={{ ...inputStyle, textAlign: 'center', fontSize: '1.4rem', letterSpacing: '0.3rem', marginBottom: '1rem' }}
            />
            {errorBox}
            <button type="submit" disabled={loading || otpCode.replace(/\s/g,'').length < 6}
              style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', background: 'var(--accent-blue, #3b82f6)', color: '#fff', border: 'none', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', opacity: (loading || otpCode.replace(/\s/g,'').length < 6) ? 0.6 : 1 }}>
              {loading ? 'Vérification...' : 'Vérifier'}
            </button>
          </form>
          {hasPhone && (
            <div style={{ marginTop: '1.2rem', textAlign: 'center' }}>
              <button onClick={handleSendSms} disabled={loading} style={{ background: 'none', border: 'none', color: 'var(--accent-blue)', fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline' }}>
                Problème avec l'application ? Recevoir un code par SMS
              </button>
            </div>
          )}
          <div style={{ marginTop: '0.8rem', textAlign: 'center' }}>
            <button onClick={() => { setStep('credentials'); setOtpCode(''); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '0.78rem', cursor: 'pointer' }}>
              ← Retour
            </button>
          </div>
        </div>
      )}

      {/* ── Étape 3 : code SMS de secours ── */}
      {step === 'sms' && (
        <div style={cardStyle}>
          {logo}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📱</div>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.4rem' }}>Code SMS de secours</h2>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0 }}>
              {smsSent ? 'Un code à 6 chiffres a été envoyé sur votre téléphone.' : 'Envoi du code en cours...'}
            </p>
          </div>
          <form onSubmit={handleVerifySms}>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" autoFocus
              value={otpCode} onChange={e => setOtpCode(e.target.value)}
              placeholder="000000" maxLength={6}
              style={{ ...inputStyle, textAlign: 'center', fontSize: '1.4rem', letterSpacing: '0.3rem', marginBottom: '1rem' }}
            />
            {errorBox}
            <button type="submit" disabled={loading || otpCode.length < 6}
              style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', background: 'var(--accent-blue, #3b82f6)', color: '#fff', border: 'none', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', opacity: (loading || otpCode.length < 6) ? 0.6 : 1 }}>
              {loading ? 'Vérification...' : 'Vérifier le code SMS'}
            </button>
          </form>
          <div style={{ marginTop: '1rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <button onClick={handleSendSms} disabled={loading} style={{ background: 'none', border: 'none', color: 'var(--accent-blue)', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline' }}>
              Renvoyer le code
            </button>
            <button onClick={() => { setStep('totp'); setOtpCode(''); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '0.78rem', cursor: 'pointer' }}>
              ← Utiliser Microsoft Authenticator
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
